import { useState } from 'react';
import type { FormEvent } from 'react';
import { useLang } from '../hooks/useLang';
import './FeedbackForm.css';

type Status = 'idle' | 'submitting' | 'ok' | 'error';

/**
 * Minimal feedback form. POSTs to the ``/api/feedback`` Pages Function,
 * which persists the submission to a KV namespace bound as ``FEEDBACK``.
 * Fields: optional name, optional email, required message. A hidden
 * ``website`` honeypot traps naive bots.
 */
export default function FeedbackForm() {
    const [lang] = useLang();
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState<string | null>(null);

    const t = lang === 'ta' ? TA : EN;

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (status === 'submitting') return;

        const form = e.currentTarget;
        const data = new FormData(form);
        const payload = {
            name: (data.get('name') || '').toString(),
            email: (data.get('email') || '').toString(),
            message: (data.get('message') || '').toString(),
            website: (data.get('website') || '').toString(), // honeypot
        };

        if (!payload.message.trim()) {
            setError(t.errorMessageRequired);
            setStatus('error');
            return;
        }

        setStatus('submitting');
        setError(null);

        try {
            const res = await fetch('/api/feedback', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const body = (await res.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(body.error || t.errorGeneric);
            }
            setStatus('ok');
            form.reset();
        } catch (err) {
            setError(
                err instanceof Error && err.message
                    ? err.message
                    : t.errorGeneric
            );
            setStatus('error');
        }
    }

    return (
        <form
            className="feedback-form"
            onSubmit={onSubmit}
            {...(lang === 'ta' ? { lang: 'ta' } : {})}
        >
            <label className="feedback-form__row">
                <span className="feedback-form__label">{t.name}</span>
                <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    maxLength={200}
                    className="feedback-form__input"
                    disabled={status === 'submitting'}
                />
            </label>

            <label className="feedback-form__row">
                <span className="feedback-form__label">{t.email}</span>
                <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    maxLength={320}
                    className="feedback-form__input"
                    disabled={status === 'submitting'}
                />
            </label>

            <label className="feedback-form__row">
                <span className="feedback-form__label">{t.message}</span>
                <textarea
                    name="message"
                    required
                    minLength={2}
                    maxLength={2000}
                    rows={5}
                    className="feedback-form__textarea"
                    disabled={status === 'submitting'}
                />
            </label>

            {/* Honeypot — hidden from users, catches naive form bots.
                Real users leave it blank; bots that fill every field
                get their submission silently discarded server-side. */}
            <label className="feedback-form__honeypot" aria-hidden="true">
                Leave this field empty
                <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                />
            </label>

            <div className="feedback-form__actions">
                <button
                    type="submit"
                    className="feedback-form__submit"
                    disabled={status === 'submitting'}
                >
                    {status === 'submitting' ? t.sending : t.send}
                </button>
                {status === 'ok' && (
                    <span className="feedback-form__status feedback-form__status--ok" role="status">
                        {t.success}
                    </span>
                )}
                {status === 'error' && error && (
                    <span className="feedback-form__status feedback-form__status--err" role="alert">
                        {error}
                    </span>
                )}
            </div>
        </form>
    );
}

// ---- localized labels ---- //

const EN = {
    name: 'Name (optional)',
    email: 'Email (optional)',
    message: 'Message',
    send: 'Send feedback',
    sending: 'Sending…',
    success: 'Thanks — got it.',
    errorGeneric: 'Something went wrong. Please try again.',
    errorMessageRequired: 'Message is required.',
};

const TA = {
    name: 'பெயர் (விருப்பம்)',
    email: 'மின்னஞ்சல் (விருப்பம்)',
    message: 'செய்தி',
    send: 'கருத்து அனுப்பு',
    sending: 'அனுப்புகிறது…',
    success: 'நன்றி — கிடைத்தது.',
    errorGeneric: 'ஏதோ தவறு நேர்ந்தது. மீண்டும் முயற்சிக்கவும்.',
    errorMessageRequired: 'செய்தி அவசியம்.',
};
